import axios from "axios";
import getAPIUrl from "@/utils/helpers/getApiUrl";
import router from '../router';
import store from '../store';
import enums from "../utils/enums/index.js";


// Normaliza y recorta mensajes de BusinessException devueltos por el backend.
// Justificación: a veces el backend retorna objetos/JSON/Blob (no string) en errores 5xx;
// esto evitaba usar .indexOf sobre algo no-string ("pString.indexOf is not a function").
// Con esta normalización garantizamos un string y devolvemos un mensaje seguro para UI.
function cutBusinessException(payload){
  try {
    // Asegurar string
    const text = typeof payload === 'string'
      ? payload
      : typeof payload === 'object' && payload !== null
        ? JSON.stringify(payload)
        : String(payload ?? '');

    const startIdx = text.indexOf('BusinessException:') !== -1
      ? text.indexOf('BusinessException:') + 'BusinessException:'.length
      : -1;
    const endIdx = text.indexOf('at GePreSalud');

    if (startIdx === -1 || endIdx === -1) return enums.messages.SYSTEM_ERROR;
    return text.slice(startIdx, endIdx).trim();
  } catch {
    return enums.messages.SYSTEM_ERROR;
  }
}

axios.interceptors.request.use(
  async (config) => {
    config.baseURL = await getAPIUrl();
    const token = sessionStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers.Accept = "application/json";
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

// Interceptor de respuestas HTTP.
// Cambios clave:
// - Uso de encadenamiento opcional (error?.response?.status) para evitar errores cuando no hay respuesta (fallas de red, CORS, timeouts)
// - Manejo uniforme de códigos 4xx/5xx y mensajes, evitando accesos a propiedades undefined
axios.interceptors.response.use(function (response) {
  return response;
}, function (error) {
  const status = error?.response?.status;

  // 503: mantenimiento controlado por API
  if (status === 503) {
    try {
      // Evitar bucle: redirigir una sola vez por sesión/ventana y respetar TTL
      const maintenanceUntil = Number(sessionStorage.getItem('maintenanceUntil') || '0');
      const alreadyRedirected = sessionStorage.getItem('maintenanceRedirected') === '1';
      const isExpired = maintenanceUntil && Date.now() > maintenanceUntil;
      if (isExpired) {
        sessionStorage.removeItem('maintenanceRedirected');
        sessionStorage.removeItem('maintenanceUntil');
      }

      if (!alreadyRedirected || isExpired) {
        const headers = error?.response?.headers || {};
        let retryAfter = headers['retry-after'] || headers['Retry-After'] || null;

        // Si Retry-After es fecha, convertir a segundos aproximados
        if (retryAfter && isNaN(retryAfter)) {
          const date = Date.parse(retryAfter);
          if (!isNaN(date)) {
            const seconds = Math.max(0, Math.round((date - Date.now())/1000));
            retryAfter = seconds;
          } else {
            retryAfter = null;
          }
        }

        const ttlMs = (retryAfter && !isNaN(Number(retryAfter))) ? Number(retryAfter)*1000 : (5*60*1000);
        const until = Date.now() + ttlMs;
        sessionStorage.setItem('maintenanceRedirected', '1');
        sessionStorage.setItem('maintenanceUntil', String(until));

        // Construir from con la ruta actual (fallback a pathname + query)
        const currentRoute = (router?.currentRoute?.value?.fullPath) || (window.location.pathname + window.location.search);
        const params = [];
        if (retryAfter) params.push(`retryAfter=${encodeURIComponent(retryAfter)}`);
        if (currentRoute) params.push(`from=${encodeURIComponent(currentRoute)}`);
        const q = params.length ? `?${params.join('&')}` : '';
        // Redirigir a la página estática
        window.location.assign('/maintenance.html' + q);
      }
    } catch (ex) {
      console.error('Error al manejar 503:', ex);
    }
    // Evitar procesamiento adicional; la página se redirige
    return new Promise(() => {});
  }
  // 401: sesión expirada/no autorizada → limpiar token y redirigir a login
  if (status === 401) {
    sessionStorage.removeItem('token');
    router.push('/login');
    return;
  }
  // 403: acceso denegado → enviar a inicio y alertar
  if (status === 403) {
    router.push('/');
    alert(enums.messages.ACCESS_DENIED);
  }
  // 4xx: advertencias de validación/cliente
  else if (status >= 400 && status < 500) {
    if (Array.isArray(error?.response?.data?.errors) && error?.response?.data !== null && error?.response?.data !== 'undefined' && error?.response?.data !== "") {
      store.commit('user/setAlertMessage', {
        type: 'warning',
        message: Array.isArray(error.response.data.errors) ? error.response.data.errors[0] : error.response.data
      }, { root: true });
    } else {
      store.commit('user/setAlertMessage', {
        type: 'warning',
        message: enums.messages.SYSTEM_ERROR,
      }, { root: true });
    }
  }
  // 5xx: error de servidor → mostrar detalle (siempre con fallback seguro)
  // Errores 5xx: muestra detalle de BusinessException si existe, sino un mensaje genérico.
  // Justificación: error.response.data puede no ser string; se normaliza para evitar excepciones
  // y darle al usuario un feedback consistente sin romper el flujo de la app.
  else if (status >= 500) {
    const raw = error?.response?.data ?? '';
    store.commit('user/setAlertMessage', {
      type: 'error',
      message: cutBusinessException(raw),
    }, { root: true });
  }
  return Promise.reject(error);
});

export default axios;
